import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, ChevronDown, User, LogIn, Handshake, TrendingUp, Building2 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { SERVICES } from '../../data/services';

const Header = () => {
  const { language, changeLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const en = language === 'en';
  const zh = language === 'zh';

  const mainNavItems = [
    { key: 'about', href: '/sobre' },
    { key: 'services', href: '/servicos', hasDropdown: 'services' },
    { key: 'network', href: '/parceiros', hasDropdown: 'network' },
    { key: 'opportunities', href: '/oportunidades' },
    { key: 'courses', href: '/cursos' },
    { key: 'events', href: '/eventos' },
    { key: 'blog', href: '/blog' },
    { key: 'contact', href: '/contato' },
  ];

  const networkItems = [
    {
      href: '/parceiros',
      icon: Handshake,
      label: en ? 'Partner Chambers' : zh ? '合作商会' : 'Câmaras Parceiras',
      desc: en ? 'Chambers and institutional partners' : zh ? '商会和机构合作伙伴' : 'Câmaras e parceiros institucionais',
    },
    {
      href: '/parceiros',
      icon: Building2,
      label: en ? 'Partners & Associates' : zh ? '合作伙伴与会员' : 'Parceiros & Associados',
      desc: en ? 'Directory of member companies' : zh ? '会员企业目录' : 'Diretório de empresas associadas',
    },
    {
      href: '/associacao',
      icon: TrendingUp,
      label: en ? 'Become a Member' : zh ? '成为会员' : 'Seja Associado',
      desc: en ? 'Membership plans and benefits' : zh ? '会员方案和权益' : 'Planos e benefícios de associação',
    },
  ];

  const languages = [
    { code: 'pt', label: 'PT', fullLabel: 'Português', flag: '🇧🇷' },
    { code: 'en', label: 'EN', fullLabel: 'English', flag: '🇺🇸' },
    { code: 'zh', label: '中文', fullLabel: '中文', flag: '🇨🇳' },
  ];

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + '/');
  const isDark = isScrolled || !isHomePage;

  const toggleDropdown = (e, name) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === name ? null : name);
  };

  const navLabel = (key) => {
    const labels = {
      about: en ? 'About' : zh ? '关于我们' : 'Quem Somos',
      services: en ? 'Services' : zh ? '服务' : 'Serviços',
      network: en ? 'Network' : zh ? '合作网络' : 'Rede',
      opportunities: en ? 'Opportunities' : zh ? '商机' : 'Oportunidades',
      courses: en ? 'Courses' : zh ? '课程' : 'Cursos',
      events: en ? 'Events' : zh ? '活动' : 'Eventos',
      blog: 'Blog',
      contact: en ? 'Contact' : zh ? '联系我们' : 'Contato',
    };
    return labels[key] || key;
  };

  return (
    <motion.header
      data-testid="header"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isDark
          ? 'bg-white shadow-lg'
          : 'bg-gradient-to-b from-black/50 to-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">

          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 lg:gap-3 flex-shrink-0"
            data-testid="logo-link"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <img
              src="/bacc-logo.jpg"
              alt="BACC Logo"
              className="h-10 w-10 lg:h-12 lg:w-12 object-contain rounded-full"
            />
            <div className="flex flex-col">
              <span className={`font-heading text-lg lg:text-xl font-bold tracking-tight transition-colors ${
                isDark ? 'text-[#1A1F4B]' : 'text-white'
              }`}>
                BACC
              </span>
              <span className={`hidden sm:block text-[9px] lg:text-[10px] leading-tight transition-colors ${
                isDark ? 'text-[#1A1F4B]/60' : 'text-white/70'
              }`}>
                Brasil Asia Chamber of Commerce
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1">
            {mainNavItems.map((item) => (
              item.hasDropdown ? (
                <div key={item.key} className="relative">
                  <button
                    onClick={(e) => toggleDropdown(e, item.hasDropdown)}
                    data-testid={`nav-${item.key}`}
                    className={`flex items-center gap-1 px-2.5 xl:px-3 py-2 text-sm font-medium transition-all rounded-md ${
                      (item.hasDropdown === 'services' && location.pathname.startsWith('/servicos')) ||
                      (item.hasDropdown === 'network' && (location.pathname === '/parceiros' || location.pathname === '/associacao'))
                        ? 'text-[#C9A84C] bg-[#C9A84C]/10'
                        : isDark
                          ? 'text-[#1A1F4B] hover:text-[#C9A84C] hover:bg-gray-100'
                          : 'text-white/90 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {navLabel(item.key)}
                    <ChevronDown className={`w-3 h-3 transition-transform ${activeDropdown === item.hasDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Services Dropdown */}
                  <AnimatePresence>
                    {activeDropdown === 'services' && item.hasDropdown === 'services' && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.97 }}
                        transition={{ duration: 0.15 }}
                        className="absolute left-0 mt-2 w-[520px] bg-white rounded-lg shadow-2xl border border-gray-100 overflow-hidden z-50"
                      >
                        <div className="p-3 bg-[#1A1F4B]">
                          <Link
                            to="/servicos"
                            onClick={() => setActiveDropdown(null)}
                            className="flex items-center justify-between px-3 py-2 text-white hover:text-[#C9A84C] transition-colors"
                          >
                            <span className="font-semibold text-sm">
                              {en ? 'View all services' : zh ? '查看所有服务' : 'Ver todos os serviços'}
                            </span>
                            <span className="text-[#C9A84C] text-xs">→</span>
                          </Link>
                        </div>
                        <div className="grid grid-cols-2 gap-px bg-gray-100 p-px">
                          {SERVICES.map((service) => {
                            const SIcon = service.icon;
                            return (
                              <Link
                                key={service.slug}
                                to={`/servicos/${service.slug}`}
                                onClick={() => setActiveDropdown(null)}
                                className="group flex items-start gap-3 px-4 py-3 bg-white hover:bg-[#F9FAFB] transition-colors"
                              >
                                <div
                                  className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5 rounded"
                                  style={{ backgroundColor: `${service.accentColor}15` }}
                                >
                                  <SIcon className="w-4 h-4" style={{ color: service.accentColor }} />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs font-semibold text-[#1A1F4B] group-hover:text-[#C9A84C] transition-colors leading-tight">
                                    {(en || zh) && service.titleEn ? service.titleEn : service.title}
                                  </p>
                                  <p className="text-[10px] text-gray-400 mt-0.5 leading-tight line-clamp-2">
                                    {(en || zh) && service.shortDescEn ? service.shortDescEn : service.shortDesc}
                                  </p>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Network Dropdown */}
                  <AnimatePresence>
                    {activeDropdown === 'network' && item.hasDropdown === 'network' && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.97 }}
                        transition={{ duration: 0.15 }}
                        className="absolute left-0 mt-2 w-72 bg-white rounded-lg shadow-2xl border border-gray-100 overflow-hidden z-50"
                      >
                        <div className="p-3 bg-[#1A1F4B]">
                          <p className="px-3 py-1 text-white/70 text-xs font-medium uppercase tracking-wide">
                            {en ? 'BACC Network' : zh ? 'BACC合作网络' : 'Rede BACC'}
                          </p>
                        </div>
                        <div className="py-1">
                          {networkItems.map((netItem, i) => {
                            const NIcon = netItem.icon;
                            return (
                              <Link
                                key={i}
                                to={netItem.href}
                                onClick={() => setActiveDropdown(null)}
                                className="group flex items-start gap-3 px-4 py-3 hover:bg-[#F9FAFB] transition-colors"
                              >
                                <div className="w-8 h-8 bg-[#C9A84C]/10 flex items-center justify-center flex-shrink-0 mt-0.5 rounded">
                                  <NIcon className="w-4 h-4 text-[#C9A84C]" />
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-[#1A1F4B] group-hover:text-[#C9A84C] transition-colors leading-tight">
                                    {netItem.label}
                                  </p>
                                  <p className="text-[11px] text-gray-400 mt-0.5 leading-tight">{netItem.desc}</p>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={item.key}
                  to={item.href}
                  data-testid={`nav-${item.key}`}
                  className={`px-2.5 xl:px-3 py-2 text-sm font-medium transition-all rounded-md whitespace-nowrap ${
                    isActive(item.href)
                      ? 'text-[#C9A84C] bg-[#C9A84C]/10'
                      : isDark
                        ? 'text-[#1A1F4B] hover:text-[#C9A84C] hover:bg-gray-100'
                        : 'text-white/90 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {navLabel(item.key)}
                </Link>
              )
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-1.5 lg:gap-2">

            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={(e) => toggleDropdown(e, 'language')}
                data-testid="language-selector"
                className={`flex items-center gap-1.5 px-2 lg:px-3 py-1.5 lg:py-2 rounded-md text-sm font-medium transition-all ${
                  isDark
                    ? 'text-[#1A1F4B] hover:bg-gray-100 border border-gray-200'
                    : 'text-white hover:bg-white/10 border border-white/30'
                }`}
              >
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline">{languages.find(l => l.code === language)?.label}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${activeDropdown === 'language' ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {activeDropdown === 'language' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden z-50"
                  >
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        data-testid={`lang-${lang.code}`}
                        onClick={() => { changeLanguage(lang.code); setActiveDropdown(null); }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                          language === lang.code
                            ? 'bg-[#1A1F4B] text-white'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span className="font-medium">{lang.fullLabel}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Member Area Dropdown */}
            <div className="relative hidden md:block">
              <button
                onClick={(e) => toggleDropdown(e, 'member')}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                  isDark
                    ? 'text-[#1A1F4B] hover:bg-gray-100 border border-gray-200'
                    : 'text-white hover:bg-white/10 border border-white/30'
                }`}
              >
                <User className="w-4 h-4" />
                <span className="hidden lg:inline">{en ? 'Members' : zh ? '会员区' : 'Associado'}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${activeDropdown === 'member' ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {activeDropdown === 'member' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-2 w-52 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden z-50"
                  >
                    <Link
                      to="/member"
                      onClick={() => setActiveDropdown(null)}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <LogIn className="w-4 h-4 text-[#1A1F4B]" />
                      <div>
                        <span className="font-medium block">{en ? 'Member Portal' : zh ? '会员门户' : 'Área do Associado'}</span>
                        <span className="text-xs text-gray-500">{en ? 'Access your account' : zh ? '访问您的账户' : 'Acesse sua conta'}</span>
                      </div>
                    </Link>
                    <div className="border-t border-gray-100">
                      <Link
                        to="/associacao"
                        onClick={() => setActiveDropdown(null)}
                        className="flex items-center gap-3 px-4 py-3 text-sm hover:bg-[#C9A84C]/10 transition-colors"
                      >
                        <div className="w-4 h-4 bg-[#C9A84C] rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs">+</span>
                        </div>
                        <div>
                          <span className="font-medium text-[#1A1F4B] block">{en ? 'Join BACC' : zh ? '加入BACC' : 'Associe-se'}</span>
                          <span className="text-xs text-gray-500">{en ? 'Membership plans' : zh ? '会员方案' : 'Planos disponíveis'}</span>
                        </div>
                      </Link>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* CTA Button */}
            <Link
              to="/associacao?associar=true"
              data-testid="header-join-btn"
              className="hidden lg:flex items-center gap-2 bg-[#C9A84C] text-[#1A1F4B] px-4 xl:px-5 py-2.5 rounded-md font-semibold text-sm hover:bg-[#D4B663] transition-all shadow-md hover:shadow-lg whitespace-nowrap"
            >
              {en ? 'Join Us' : zh ? '加入我们' : 'Associe-se'}
            </Link>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 rounded-md transition-colors ${
                isDark ? 'text-[#1A1F4B] hover:bg-gray-100' : 'text-white hover:bg-white/10'
              }`}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-100 shadow-lg"
            data-testid="mobile-menu"
          >
            <nav className="px-4 py-4">
              <div className="space-y-1 mb-4">
                {mainNavItems.map((item) => (
                  item.hasDropdown ? (
                    <div key={item.key}>
                      <button
                        onClick={(e) => toggleDropdown(e, `mobile-${item.hasDropdown}`)}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-lg font-medium transition-colors ${
                          (item.hasDropdown === 'services' && location.pathname.startsWith('/servicos')) ||
                          (item.hasDropdown === 'network' && (location.pathname === '/parceiros' || location.pathname === '/associacao'))
                            ? 'text-[#C9A84C] bg-[#C9A84C]/10'
                            : 'text-[#1A1F4B] hover:bg-gray-50'
                        }`}
                      >
                        <span>{navLabel(item.key)}</span>
                        <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === `mobile-${item.hasDropdown}` ? 'rotate-180' : ''}`} />
                      </button>
                      <AnimatePresence>
                        {activeDropdown === `mobile-${item.hasDropdown}` && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="ml-4 mt-1 border-l-2 border-[#C9A84C]/30 pl-3 space-y-1"
                          >
                            {item.hasDropdown === 'services' && (
                              <>
                                <Link to="/servicos" onClick={() => { setIsMobileMenuOpen(false); setActiveDropdown(null); }}
                                  className="block px-3 py-2 text-sm text-[#1A1F4B] font-semibold hover:text-[#C9A84C] transition-colors">
                                  {en ? 'View all services →' : zh ? '查看所有服务 →' : 'Ver todos os serviços →'}
                                </Link>
                                {SERVICES.map((service) => (
                                  <Link key={service.slug} to={`/servicos/${service.slug}`}
                                    onClick={() => { setIsMobileMenuOpen(false); setActiveDropdown(null); }}
                                    className="block px-3 py-2 text-sm text-[#1F2937]/70 hover:text-[#C9A84C] transition-colors">
                                    {service.flag} {(en || zh) && service.titleEn ? service.titleEn : service.title}
                                  </Link>
                                ))}
                              </>
                            )}
                            {item.hasDropdown === 'network' && networkItems.map((netItem, i) => (
                              <Link key={i} to={netItem.href} onClick={() => { setIsMobileMenuOpen(false); setActiveDropdown(null); }}
                                className="block px-3 py-2 text-sm text-[#1F2937]/70 hover:text-[#C9A84C] transition-colors">
                                {netItem.label}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      key={item.key}
                      to={item.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      data-testid={`mobile-nav-${item.key}`}
                      className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                        isActive(item.href)
                          ? 'text-[#C9A84C] bg-[#C9A84C]/10'
                          : 'text-[#1A1F4B] hover:bg-gray-50'
                      }`}
                    >
                      {navLabel(item.key)}
                    </Link>
                  )
                ))}
              </div>

              <div className="border-t border-gray-100 my-4" />

              <div className="space-y-2">
                <Link to="/member" onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#1A1F4B] hover:bg-gray-50 transition-colors">
                  <User className="w-5 h-5" />
                  <span className="font-medium">{en ? 'Member Portal' : zh ? '会员门户' : 'Área do Associado'}</span>
                </Link>
                <Link to="/associacao?associar=true" onClick={() => setIsMobileMenuOpen(false)}
                  data-testid="mobile-join-btn"
                  className="flex items-center justify-center gap-2 bg-[#C9A84C] text-[#1A1F4B] px-6 py-3 rounded-lg font-semibold">
                  {en ? 'Join BACC' : zh ? '加入BACC' : 'Associe-se'}
                </Link>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
